import * as ExcelJS from 'exceljs';
import { TransactionResponse } from '../../model/Transaction.model';
import { BaseExcelService } from './baseExcelService';
import { ExcelColors } from './types';

/**
 * Service for exporting withdrawal transaction to Excel
 */
export class WithdrawalExportService {
  /**
   * Export withdrawal transaction to Excel invoice
   */
  static async exportToExcel(transaction: TransactionResponse): Promise<void> {
    console.log('Export Excel - Withdrawal transaction data:', transaction);

    // Calculate amounts
    const {
      amount,
      feeForCustomer,
      customerType,
      feeApplied,
      balance,
      commissionRateForPartner,
    } = transaction;

    // Calculate thành tiền (total amount for customer fee)
    let totalAmount = 0;
    if (feeForCustomer) {
      if (customerType === 'vn') {
        // Phí VND: Thành tiền = Phí KH (đã là số tiền cụ thể)
        totalAmount = feeForCustomer;
      } else {
        // Phí %: Thành tiền = Số tiền rút × (Phí KH %)
        totalAmount = amount * (feeForCustomer / 100);
      }
    }

    // Calculate commission for partner (CTV)
    const partnerCommission = commissionRateForPartner
      ? (amount * commissionRateForPartner) / 100
      : 0;

    // Calculate POS fee amount
    const posFeeAmount = feeApplied ? (amount * feeApplied) / 100 : 0;

    console.log('Calculated values:', {
      amount,
      feeForCustomer,
      customerType,
      totalAmount,
      partnerCommission,
      posFeeAmount,
      balance,
    });

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Hóa đơn');

    // Set column widths
    worksheet.columns = [
      { width: 25 }, // A - Dịch vụ
      { width: 15 }, // B - Số tiền
      { width: 12 }, // C - Đơn vị
      { width: 10 }, // D - Phí (%)
    ];

    // 1. Main title
    worksheet.mergeCells('A1:D1');
    BaseExcelService.createHeaderCell(
      worksheet,
      'A1',
      'HÓA ĐƠN DỊCH VỤ RÚT TIỀN THẺ TÍN DỤNG',
      16,
      ExcelColors.PRIMARY_HEADER
    );

    // 2. Export date
    WithdrawalExportService.addExportDate(worksheet);

    // 3. Customer and card information section
    WithdrawalExportService.addTransactionInfo(worksheet, transaction);

    // 4. Service details section
    WithdrawalExportService.addServiceDetails(worksheet, {
      amount,
      feeForCustomer: feeForCustomer || 0,
      customerType: customerType || '%',
      totalAmount,
      partnerCommission,
      posFeeAmount,
      feeApplied: feeApplied || 0,
      commissionRateForPartner: commissionRateForPartner || 0,
      balance,
    });

    // 5. Total amount
    WithdrawalExportService.addTotalAmount(worksheet, totalAmount);

    // Generate and save file
    const fileName = WithdrawalExportService.generateFileName(transaction);
    await BaseExcelService.saveExcelFile(workbook, fileName);
  }

  /**
   * Add export date section
   */
  private static addExportDate(worksheet: ExcelJS.Worksheet): void {
    worksheet.getCell('A3').value = 'Ngày xuất:';
    worksheet.getCell('B3').value = BaseExcelService.formatVietnameseDate(
      new Date()
    );
  }

  /**
   * Add transaction information section
   */
  private static addTransactionInfo(
    worksheet: ExcelJS.Worksheet,
    transaction: TransactionResponse
  ): void {
    // Transaction header
    worksheet.mergeCells('A5:D5');
    BaseExcelService.createHeaderCell(
      worksheet,
      'A5',
      'THÔNG TIN GIAO DỊCH',
      12,
      ExcelColors.SECONDARY_HEADER
    );

    // Transaction details
    const transactionInfo = [
      ['Mã giao dịch:', `#${transaction.id}`],
      [
        'Ngày giao dịch:',
        BaseExcelService.formatVietnameseDate(
          new Date(transaction.transactionDate)
        ),
      ],
      ['Số thẻ:', transaction.creditCard?.cardNumber || 'N/A'],
      ['Chủ thẻ:', transaction.creditCard?.cardHolderName || 'N/A'],
      ['Khách hàng:', transaction.creditCard?.cardHolderName || 'N/A'],
      [
        'Trạng thái:',
        transaction.status === 'COMPLETED' ? 'Hoàn thành' : 'Đang chờ',
      ],
    ];

    transactionInfo.forEach((info, index) => {
      const row = 6 + index;
      worksheet.getCell(`A${row}`).value = info[0];
      worksheet.getCell(`A${row}`).font = { bold: true };
      worksheet.getCell(`B${row}`).value = info[1];
    });
  }

  /**
   * Add service details section
   */
  private static addServiceDetails(
    worksheet: ExcelJS.Worksheet,
    amounts: {
      amount: number;
      feeForCustomer: number;
      customerType: string;
      totalAmount: number;
      partnerCommission: number;
      posFeeAmount: number;
      feeApplied: number;
      commissionRateForPartner: number;
      balance: number;
    }
  ): void {
    // Service header
    worksheet.mergeCells('A13:D13');
    BaseExcelService.createHeaderCell(
      worksheet,
      'A13',
      'CHI TIẾT DỊCH VỤ',
      12,
      ExcelColors.SECONDARY_HEADER
    );

    // Table headers
    const tableHeaders = ['Dịch vụ', 'Số tiền', 'Đơn vị', 'Phí (%)'];
    BaseExcelService.createTableHeader(worksheet, 15, tableHeaders);

    // Table data - only show required rows
    const tableData = [['Số tiền rút', amounts.amount, 'VND', '']];

    // Only add customer fee row if it's percentage type
    if (amounts.customerType === '%') {
      tableData.push([
        'Phí khách hàng',
        amounts.feeForCustomer,
        '%',
        amounts.feeForCustomer,
      ]);
    }

    // Add POS fee and total amount
    tableData.push(
      ['Phí POS', amounts.posFeeAmount, 'VND', amounts.feeApplied],
      ['Thành tiền', amounts.totalAmount, 'VND', '']
    );

    tableData.forEach((rowData, rowIndex) => {
      const row = 16 + rowIndex;
      rowData.forEach((cellData, colIndex) => {
        const cell = worksheet.getCell(row, colIndex + 1);
        cell.value = cellData;

        // Format currency
        if (colIndex === 1 && typeof cellData === 'number') {
          BaseExcelService.formatCurrencyCell(cell, cellData);
        }
        // Format percentage
        else if (colIndex === 3 && typeof cellData === 'number') {
          BaseExcelService.formatPercentageCell(cell, cellData);
        }
        // Apply border for other cells
        else {
          BaseExcelService.applyBorder(cell);
        }
      });
    });
  }

  /**
   * Add total amount section
   */
  private static addTotalAmount(
    worksheet: ExcelJS.Worksheet,
    totalAmount: number
  ): void {
    const totalRow = 20;

    // Total label
    const labelCell = worksheet.getCell(`A${totalRow}`);
    labelCell.value = 'TỔNG THÀNH TIỀN';
    BaseExcelService.applyCellStyle(labelCell, {
      font: { bold: true, color: { argb: ExcelColors.WHITE_TEXT } },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: ExcelColors.PRIMARY_HEADER },
      },
    });

    // Total amount
    const amountCell = worksheet.getCell(`B${totalRow}`);
    amountCell.value = totalAmount;
    amountCell.numFmt = '#,##0';
    BaseExcelService.applyCellStyle(amountCell, {
      font: { bold: true, color: { argb: ExcelColors.WHITE_TEXT } },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: ExcelColors.PRIMARY_HEADER },
      },
    });

    // Apply border to all cells in total row
    ['A', 'B', 'C', 'D'].forEach((col) => {
      BaseExcelService.applyBorder(worksheet.getCell(`${col}${totalRow}`));
    });
  }

  /**
   * Generate file name for withdrawal export
   */
  private static generateFileName(transaction: TransactionResponse): string {
    const cardNumber = transaction.creditCard?.cardNumber?.slice(-4) || 'XXXX';
    const transactionId = transaction.id;
    const transactionDate = new Date(transaction.transactionDate)
      .toISOString()
      .split('T')[0];

    return BaseExcelService.generateFileName(
      `HoaDon_RutTien_${transactionId}_${cardNumber}_${transactionDate}`
    );
  }
}
